"use client";

import {
  COMPANY_EMAILS,
  INQUIRY_PARAM_TO_TYPE,
  INQUIRY_TYPES,
  type InquiryType,
} from "@repo/common";
import { Button } from "@repo/ui";
import { Input } from "@repo/ui";
import { Label } from "@repo/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui";
import { Textarea } from "@repo/ui";
import { ArrowRight, CheckCircle2, ChevronDown, Loader2 } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { api } from "@/lib/trpc/react";

const EMPTY_FORM: {
  fullName: string;
  workEmail: string;
  country: string;
  jobFunction: string;
  inquiryType: InquiryType | "";
  message: string;
} = {
  fullName: "",
  workEmail: "",
  country: "",
  jobFunction: "",
  inquiryType: "",
  message: "",
};

// Most non-buyer submissions are experts: people asking about their
// application, payouts, verification codes, or account deletion, or looking for
// paid work. This option is client-only (not an InquiryType) so those people
// are pointed at opportunities and support instead of emailing sales.
const EXPERT_OPTION = "I'm an expert or looking for paid work";

const ExpertHelpPanel = () => (
  <div className="bg-muted/50 ring-foreground/10 space-y-4 rounded-lg p-5 ring-1">
    <p className="text-sm">
      This form goes to our sales team, who can&apos;t help with expert
      accounts. Here&apos;s where to go instead:
    </p>
    <div className="flex flex-wrap gap-2">
      <Button asChild>
        <Link href="/opportunities">
          Browse paid opportunities
          <ArrowRight className="ml-2 size-4" />
        </Link>
      </Button>
      <Button asChild variant="outline">
        <Link href="/help">Get help with my account</Link>
      </Button>
    </div>
    <p className="text-muted-foreground text-sm">
      For applications, payments, verification, or deleting your account, email{" "}
      <a
        href={`mailto:${COMPANY_EMAILS.support}`}
        className="text-primary underline"
      >
        {COMPANY_EMAILS.support}
      </a>
      .
    </p>
  </div>
);

export const InquiryForm = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isExpert, setIsExpert] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showInquiryError, setShowInquiryError] = useState(false);

  // Preselect the inquiry type from ?inquiry=. Reading from window (rather than
  // useSearchParams) avoids forcing a Suspense boundary on this static route.
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get("inquiry");
    const matched = param ? INQUIRY_PARAM_TO_TYPE[param] : undefined;
    if (matched) {
      setFormData((prev) => ({ ...prev, inquiryType: matched }));
    }
  }, []);

  const sendInquiry = api.marketing.sendSalesInquiry.useMutation({
    onSuccess: () => {
      setIsSubmitted(true);
      setFormData(EMPTY_FORM);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isExpert) {
      return;
    }
    const { inquiryType } = formData;
    if (!inquiryType) {
      setShowInquiryError(true);
      return;
    }
    sendInquiry.mutate({ ...formData, inquiryType });
  };

  if (isSubmitted) {
    return (
      <div className="relative">
        <div className="absolute -inset-6 mask-b-from-50% px-6 pt-4">
          <div className="bg-card/75 ring-border-illustration size-full rounded-t-2xl" />
        </div>
        <div className="relative flex flex-col items-center justify-center space-y-4 p-8 text-center">
          <div className="rounded-full bg-emerald-100 p-4 dark:bg-emerald-900/30">
            <CheckCircle2 className="size-12 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-heading-24 font-medium text-balance">
            Thank you!
          </h3>
          <p className="text-muted-foreground">
            We&apos;ve received your inquiry and will get back to you within 24
            hours.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              setIsSubmitted(false);
            }}
            className="mt-4"
          >
            Send another message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute -inset-6 mask-b-from-50% px-6 pt-4">
        <div className="bg-card/75 ring-border-illustration size-full rounded-t-2xl" />
      </div>
      <form
        onSubmit={handleSubmit}
        className="relative space-y-6 p-8 *:space-y-3 **:[&>label]:block"
      >
        <div className="grid gap-3 *:space-y-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input
              type="text"
              id="name"
              className="ring-foreground/10 bg-background border-transparent ring-1"
              required
              value={formData.fullName}
              onChange={(e) => {
                setFormData({ ...formData, fullName: e.target.value });
              }}
              disabled={sendInquiry.isPending}
            />
          </div>

          <div>
            <Label htmlFor="email">Work Email</Label>
            <Input
              type="email"
              id="email"
              className="ring-foreground/10 bg-background border-transparent ring-1"
              required
              value={formData.workEmail}
              onChange={(e) => {
                setFormData({ ...formData, workEmail: e.target.value });
              }}
              disabled={sendInquiry.isPending}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="country">Country/Region</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="bg-background ring-offset-background border-input ring-foreground/10 w-full justify-between text-left font-normal ring-1"
                disabled={sendInquiry.isPending}
              >
                {formData.country || "Select Country/Region"}
                <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="max-h-[300px] w-[var(--radix-popper-anchor-width)] overflow-y-auto"
              align="start"
            >
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, country: "United States" });
                }}
              >
                🇺🇸 United States
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, country: "Canada" });
                }}
              >
                🇨🇦 Canada
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, country: "United Kingdom" });
                }}
              >
                🇬🇧 United Kingdom
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, country: "Australia" });
                }}
              >
                🇦🇺 Australia
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, country: "Germany" });
                }}
              >
                🇩🇪 Germany
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, country: "France" });
                }}
              >
                🇫🇷 France
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, country: "Japan" });
                }}
              >
                🇯🇵 Japan
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, country: "Other" });
                }}
              >
                🌍 Other
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div>
          <Label htmlFor="job">Job function</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="bg-background ring-offset-background border-input ring-foreground/10 w-full justify-between text-left font-normal ring-1"
                disabled={sendInquiry.isPending}
              >
                {formData.jobFunction || "Select Job Function"}
                <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[var(--radix-popper-anchor-width)]"
              align="start"
            >
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, jobFunction: "Product" });
                }}
              >
                Product Management
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, jobFunction: "Engineering" });
                }}
              >
                Engineering
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, jobFunction: "Research" });
                }}
              >
                Research & Design
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, jobFunction: "Marketing" });
                }}
              >
                Marketing
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, jobFunction: "Sales" });
                }}
              >
                Sales
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, jobFunction: "Executive" });
                }}
              >
                Executive/C-Suite
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, jobFunction: "Operations" });
                }}
              >
                Operations
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFormData({ ...formData, jobFunction: "Other" });
                }}
              >
                Other
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div>
          <Label htmlFor="inquiry">
            Inquiry type <span className="text-destructive">*</span>
          </Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="bg-background ring-offset-background border-input ring-foreground/10 w-full justify-between text-left font-normal ring-1"
                disabled={sendInquiry.isPending}
              >
                {isExpert
                  ? EXPERT_OPTION
                  : formData.inquiryType || "What can we help with?"}
                <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[var(--radix-popper-anchor-width)]"
              align="start"
            >
              <DropdownMenuItem
                onClick={() => {
                  setIsExpert(true);
                  setShowInquiryError(false);
                }}
              >
                {EXPERT_OPTION}
              </DropdownMenuItem>
              {INQUIRY_TYPES.map((type) => (
                <DropdownMenuItem
                  key={type}
                  onClick={() => {
                    setIsExpert(false);
                    setFormData({ ...formData, inquiryType: type });
                    setShowInquiryError(false);
                  }}
                >
                  {type}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {showInquiryError && (
            <p className="text-destructive text-sm">
              Please select an inquiry type.
            </p>
          )}
        </div>

        {isExpert ? (
          <ExpertHelpPanel />
        ) : (
          <>
            <div>
              <Label htmlFor="msg">Message</Label>
              <Textarea
                id="msg"
                rows={3}
                className="ring-foreground/10 bg-background border-transparent ring-1"
                placeholder="Tell us about your research needs..."
                value={formData.message}
                onChange={(e) => {
                  setFormData({ ...formData, message: e.target.value });
                }}
                disabled={sendInquiry.isPending}
              />
            </div>

            {sendInquiry.isError && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
                {sendInquiry.error.message ||
                  `Failed to send inquiry. Please try again or email us at ${COMPANY_EMAILS.support}`}
              </div>
            )}

            <div className="grid gap-6 sm:grid-cols-[1fr_auto]">
              <p className="text-muted-foreground text-sm">
                By submitting this form, you agree to our{" "}
                <Link
                  href="/legal/privacy-policy"
                  className="text-primary underline"
                >
                  Privacy Policy
                </Link>
              </p>
              <Button
                type="submit"
                className="max-sm:row-start-1"
                disabled={sendInquiry.isPending}
              >
                {sendInquiry.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Get in touch"
                )}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};
